import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, DollarSign, CreditCard, Building2, CheckCircle, Clock, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import { parseLocalDate, formatLocalDate, getMexicoTodayDateOnly } from '@/lib/utils';
import { SerieFolioSelector, type AvailableSeries } from '@/components/treasury/SerieFolioSelector';

interface InvoiceItem {
  product?: {
    id?: number;
    name?: string;
    allowedPaymentMethods?: string;
  };
}

interface Invoice {
  id: number;
  invoiceNumber: string;
  total: string;
  status: string;
  customer: {
    nombre: string;
    apellidoPaterno: string;
  };
  items?: InvoiceItem[];
}

interface Account {
  id: number;
  code: string;
  name: string;
  type: string;
}

interface PaymentSummary {
  invoiceTotal: number;
  totalPaid: number;
  pendingAmount: number;
}

interface TuitionPricing {
  installmentNumber: number;
  planId: number;
  baseAmount: number;
  discountAmount: number;
  surchargeAmount: number;
  dueDate: string;
  graceDeadline: string;
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice;
  onSuccess: () => void;
}

async function fetchBankAccounts(): Promise<Account[]> {
  const response = await fetch('/api/finance/accounts', { credentials: 'include' });
  if (!response.ok) throw new Error('Error al cargar cuentas');
  const accounts = await response.json();
  return accounts.filter((acc: Account) => acc.type === 'ASSET');
}

async function fetchPaymentSummary(invoiceId: number): Promise<PaymentSummary> {
  const response = await fetch(`/api/treasury/invoices/${invoiceId}/payment-summary`, { credentials: 'include' });
  if (!response.ok) throw new Error('Error al cargar resumen de pagos');
  return response.json();
}

async function fetchTuitionPricing(invoiceId: number): Promise<TuitionPricing | null> {
  const response = await fetch(`/api/treasury/invoices/${invoiceId}/tuition-pricing`, { credentials: 'include' });
  if (!response.ok) return null;
  return response.json();
}

async function fetchAvailableSeries(invoiceId: number): Promise<AvailableSeries> {
  const response = await fetch(`/api/treasury/invoices/${invoiceId}/available-series`, { credentials: 'include' });
  if (!response.ok) return { invoicingSettingsId: null, razonSocialName: null, series: [], defaults: [] };
  return response.json();
}

interface PaymentPromotion {
  id: number;
  name: string;
  code: string | null;
  discountType: 'PERCENTAGE' | 'FIXED';
  discountValue: string;
  maxDiscountAmount: string | null;
  applicablePaymentMethods: string[] | null;
  stackable: boolean;
}

async function fetchActivePaymentPromotions(): Promise<PaymentPromotion[]> {
  const r = await fetch('/api/promotions/active', { credentials: 'include' });
  if (!r.ok) return [];
  const all = await r.json();
  return all.filter((p: any) => p.type === 'forma_pago');
}

async function registerPayment(data: {
  invoiceId: number;
  date: string;
  amount: number;
  paymentMethod: string;
  bankAccountId: number;
  reference?: string;
  promotionId?: number;
  serieId?: number;
}) {
  const response = await fetch('/api/treasury/payments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al registrar pago');
  }
  return response.json();
}

const allPaymentMethods = [
  { value: 'TRANSFER', label: 'Transferencia' },
  { value: 'CASH', label: 'Efectivo' },
  { value: 'CHECK', label: 'Cheque' },
  { value: 'CARD', label: 'Tarjeta' },
  { value: 'OTHER', label: 'Otro' },
];

function getAvailablePaymentMethods(invoice: Invoice) {
  if (!invoice.items || invoice.items.length === 0) return allPaymentMethods;
  let allowedSet: Set<string> | null = null;
  for (const item of invoice.items) {
    if (item.product?.allowedPaymentMethods) {
      try {
        const methods: string[] = JSON.parse(item.product.allowedPaymentMethods);
        allowedSet = allowedSet === null ? new Set(methods) : new Set([...allowedSet].filter(m => methods.includes(m)));
      } catch (e) { /* ignore */ }
    }
  }
  if (allowedSet === null || allowedSet.size === 0) return allPaymentMethods;
  return allPaymentMethods.filter(m => allowedSet!.has(m.value));
}

type Scenario = 'A' | 'B' | 'C';

function getScenario(paymentDate: string, pricing: TuitionPricing): Scenario {
  const pd = parseLocalDate(paymentDate);
  const due = parseLocalDate(pricing.dueDate);
  const grace = parseLocalDate(pricing.graceDeadline);
  pd.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  grace.setHours(0, 0, 0, 0);
  if (pd <= due) return 'A';
  if (pd <= grace) return 'B';
  return 'C';
}

function getApplicableAmount(scenario: Scenario, pricing: TuitionPricing, invoiceTotal: number, pendingAmount: number): number {
  const ratio = invoiceTotal / pricing.baseAmount;
  if (scenario === 'A') {
    // Same criterion as invoice.service.ts's "pronto" pricingInfo: the discounted price is
    // a fixed target, not a per-payment amount — subtract what's already been paid so a
    // second (partial-balance) payment inside the pronto-pago window doesn't re-charge the
    // portion already collected.
    const discountWithTax = Math.round(pricing.discountAmount * ratio * 100) / 100;
    const totalPaid = invoiceTotal - pendingAmount;
    return Math.max(0, Math.round((discountWithTax - totalPaid) * 100) / 100);
  }
  if (scenario === 'C') {
    // El recargo se prorratea sobre el saldo real pendiente, no sobre la cuota completa —
    // si ya se pagó la mayor parte a tiempo, el recargo solo debe cobrarse sobre lo que falta.
    const surchargePct = pricing.surchargeAmount / pricing.baseAmount - 1;
    return Math.round(pendingAmount * (1 + surchargePct) * 100) / 100;
  }
  return invoiceTotal;
}

const scenarioConfig = {
  A: {
    icon: CheckCircle,
    color: 'text-green-700',
    bg: 'bg-green-50 border-green-200',
    label: 'Escenario A — Pronto Pago',
    desc: (pct: number) => `Pago dentro del vencimiento: precio con ${pct}% de descuento`,
  },
  B: {
    icon: Clock,
    color: 'text-yellow-700',
    bg: 'bg-yellow-50 border-yellow-200',
    label: 'Escenario B — Precio Normal',
    desc: (_pct: number) => 'Dentro del período de gracia: sin descuento ni recargo',
  },
  C: {
    icon: AlertTriangle,
    color: 'text-red-700',
    bg: 'bg-red-50 border-red-200',
    label: 'Escenario C — Recargo por Mora',
    desc: (pct: number) => `Fuera del período de gracia: precio con ${pct}% de recargo`,
  },
};

export default function PaymentModal({ isOpen, onClose, invoice, onSuccess }: PaymentModalProps) {
  const { toast } = useToast();
  const today = getMexicoTodayDateOnly();

  const [formData, setFormData] = useState({
    date: today,
    amount: '',
    paymentMethod: 'TRANSFER',
    bankAccountId: '',
    reference: '',
  });

  const { data: bankAccounts = [], isLoading: loadingAccounts } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: fetchBankAccounts,
    enabled: isOpen,
  });

  const { data: paymentSummary, isLoading: loadingSummary, refetch: refetchSummary } = useQuery({
    queryKey: ['payment-summary', invoice.id],
    queryFn: () => fetchPaymentSummary(invoice.id),
    enabled: isOpen,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: tuitionPricing } = useQuery({
    queryKey: ['tuition-pricing', invoice.id],
    queryFn: () => fetchTuitionPricing(invoice.id),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const { data: paymentPromotions = [] } = useQuery({
    queryKey: ['promotions-active-payment'],
    queryFn: fetchActivePaymentPromotions,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const { data: availableSeries } = useQuery({
    queryKey: ['available-series', invoice.id],
    queryFn: () => fetchAvailableSeries(invoice.id),
    enabled: isOpen,
    staleTime: 60 * 1000,
  });

  const [manualSerieId, setManualSerieId] = useState<number | null>(null);

  const autoSerieId = useMemo(() => {
    if (!availableSeries) return null;
    return availableSeries.defaults.find((d) => d.paymentMethod === formData.paymentMethod)?.seriesId ?? null;
  }, [availableSeries, formData.paymentMethod]);

  const effectiveSerieId = manualSerieId ?? autoSerieId;

  useEffect(() => {
    setManualSerieId(null); // reset manual override when payment method changes, so autoSerieId takes over again
  }, [formData.paymentMethod]);

  const applicablePaymentPromo = useMemo<PaymentPromotion | null>(() => {
    if (!paymentPromotions.length) return null;
    return paymentPromotions.find(p =>
      !p.applicablePaymentMethods || p.applicablePaymentMethods.length === 0 ||
      p.applicablePaymentMethods.includes(formData.paymentMethod)
    ) || null;
  }, [paymentPromotions, formData.paymentMethod]);

  const scenario = useMemo<Scenario | null>(() => {
    if (!tuitionPricing || !formData.date) return null;
    return getScenario(formData.date, tuitionPricing);
  }, [tuitionPricing, formData.date]);

  const applicableAmount = useMemo<number | null>(() => {
    if (!tuitionPricing || !scenario || !paymentSummary) return null;
    const total = paymentSummary.invoiceTotal;
    return getApplicableAmount(scenario, tuitionPricing, total, paymentSummary.pendingAmount);
  }, [tuitionPricing, scenario, paymentSummary]);

  // Escenario A (pronto pago) y C (recargo por mora) ya traen su propio descuento/recargo
  // calculado en el backend. Escenario B (precio normal) y las facturas sin colegiatura no
  // tienen ningún descuento con el que competir, así que ahí la promoción forma_pago siempre
  // se puede aplicar. En Escenario A solo se permite cuando la propia promoción está marcada
  // como `stackable` (apilable) — confirmado con el cliente (Carlos Sanchez, gqb, 2026-09-09):
  // el descuento por forma de pago se calcula SOBRE el precio ya descontado por pronto pago
  // (descuento sobre descuento), nunca sumado como un porcentaje adicional sobre el precio base.
  const promoAllowedForScenario = applicableAmount === null || scenario === 'B'
    || (scenario === 'A' && !!applicablePaymentPromo?.stackable);

  // Base sobre la que se calcula el % de la promoción forma_pago: en Escenario A, el precio
  // YA descontado por pronto pago (applicableAmount); en cualquier otro caso, el saldo pendiente.
  const promoBaseAmount = useMemo<number>(() => {
    if (scenario === 'A' && applicableAmount !== null) return applicableAmount;
    return paymentSummary?.pendingAmount ?? 0;
  }, [scenario, applicableAmount, paymentSummary]);

  const paymentPromoDiscount = useMemo<number>(() => {
    if (!applicablePaymentPromo || !paymentSummary) return 0;
    const base = promoBaseAmount;
    if (applicablePaymentPromo.discountType === 'PERCENTAGE') {
      let disc = base * parseFloat(applicablePaymentPromo.discountValue) / 100;
      if (applicablePaymentPromo.maxDiscountAmount) {
        disc = Math.min(disc, parseFloat(applicablePaymentPromo.maxDiscountAmount));
      }
      return disc;
    } else {
      return Math.min(parseFloat(applicablePaymentPromo.discountValue), base);
    }
  }, [applicablePaymentPromo, paymentSummary, promoBaseAmount]);

  const discountPct = useMemo(() => {
    if (!tuitionPricing) return 0;
    return Math.round((1 - tuitionPricing.discountAmount / tuitionPricing.baseAmount) * 100);
  }, [tuitionPricing]);

  const surchargePct = useMemo(() => {
    if (!tuitionPricing) return 0;
    return Math.round((tuitionPricing.surchargeAmount / tuitionPricing.baseAmount - 1) * 100);
  }, [tuitionPricing]);

  useEffect(() => {
    if (isOpen) refetchSummary();
  }, [isOpen, refetchSummary]);

  useEffect(() => {
    if (paymentSummary) {
      let baseAmount: number;
      if (applicableAmount !== null) {
        if (scenario === 'C') {
          baseAmount = applicableAmount;
        } else if (promoAllowedForScenario) {
          // Escenario B (sin descuento/recargo que proteger), o Escenario A con promo
          // apilable: descuento sobre descuento — la promo se resta sobre el precio ya
          // ajustado por el escenario (pronto pago), no sobre el precio base original.
          baseAmount = Math.max(0, Math.min(applicableAmount, paymentSummary.pendingAmount) - paymentPromoDiscount);
        } else {
          // Escenario A sin promo apilable: el precio de pronto pago ya es el monto a cobrar
          baseAmount = Math.min(applicableAmount, paymentSummary.pendingAmount);
        }
      } else {
        // No tuition scenario: auto-deduct forma_pago promo discount from suggested amount
        baseAmount = Math.max(0, paymentSummary.pendingAmount - paymentPromoDiscount);
      }
      setFormData(prev => ({ ...prev, amount: baseAmount.toFixed(2) }));
    }
  }, [paymentSummary, applicableAmount, scenario, paymentPromoDiscount, promoAllowedForScenario]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({ date: today, amount: '', paymentMethod: 'TRANSFER', bankAccountId: '', reference: '' });
    } else {
      const availableMethods = getAvailablePaymentMethods(invoice);
      if (!availableMethods.some(m => m.value === formData.paymentMethod)) {
        setFormData(prev => ({ ...prev, paymentMethod: availableMethods[0]?.value || 'TRANSFER' }));
      }
    }
  }, [isOpen, today, invoice]);

  const paymentMutation = useMutation({
    mutationFn: registerPayment,
    onSuccess: (data) => {
      toast({
        title: 'Pago registrado',
        description: `Pago registrado exitosamente. Estado: ${data.invoiceStatus === 'PAID' ? 'Pagada' : 'Pago Parcial'}`,
      });
      onSuccess();
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.bankAccountId) {
      toast({ title: 'Error', description: 'Seleccione una cuenta de destino', variant: 'destructive' });
      return;
    }
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast({ title: 'Error', description: 'Ingrese un monto válido', variant: 'destructive' });
      return;
    }
    // Only send promotionId when it can't stack with a competing tuition write-off.
    // Escenario A (pronto pago) and C (recargo por mora) already carry their own
    // discount/surcharge write-off in the backend — stacking a forma_pago promo on
    // top of those would double-discount. Escenario B (precio normal) and non-tuition
    // invoices have no such write-off, so the promo is safe to combine there.
    const promoIdToSend = promoAllowedForScenario ? applicablePaymentPromo?.id : undefined;
    paymentMutation.mutate({
      invoiceId: invoice.id,
      date: formData.date,
      amount,
      paymentMethod: formData.paymentMethod,
      bankAccountId: parseInt(formData.bankAccountId),
      reference: formData.reference || undefined,
      promotionId: promoIdToSend,
      serieId: effectiveSerieId ?? undefined,
    });
  };

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

  const isLoading = loadingAccounts || loadingSummary;
  const scCfg = scenario ? scenarioConfig[scenario] : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Registrar Pago
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-500 mt-2">Cargando información...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Invoice summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Factura:</span>
                <span className="font-mono font-medium">{invoice.invoiceNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Cliente:</span>
                <span className="font-medium">
                  {invoice.customer?.nombre} {invoice.customer?.apellidoPaterno}
                </span>
              </div>
              {availableSeries?.razonSocialName && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Razón Social:</span>
                  <span className="font-medium">{availableSeries.razonSocialName}</span>
                </div>
              )}
              {tuitionPricing && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Cuota:</span>
                  <span className="text-gray-700">#{tuitionPricing.installmentNumber} — Vence {formatLocalDate(tuitionPricing.dueDate, { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total Factura:</span>
                  <span className="font-mono">{formatCurrency(paymentSummary?.invoiceTotal || 0)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Ya Pagado:</span>
                  <span className="font-mono text-green-600">{formatCurrency(paymentSummary?.totalPaid || 0)}</span>
                </div>
                <div className="flex justify-between text-sm font-bold mt-1 pt-1 border-t">
                  <span className="text-gray-700">Saldo Pendiente:</span>
                  <span className="font-mono text-blue-600">{formatCurrency(paymentSummary?.pendingAmount || 0)}</span>
                </div>
              </div>
            </div>

            {/* Date + amount */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Fecha de Pago</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  max={today}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Monto a Pagar</Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400">$</span>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="0.00"
                    className="pl-7 font-mono"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Forma de pago promotion banner — hidden only when it would stack with a tuition write-off (Escenario A/C) */}
            {applicablePaymentPromo && paymentPromoDiscount > 0 && promoAllowedForScenario && (
              <div className="rounded-lg border bg-violet-50 border-violet-200 p-3 flex items-start gap-3">
                <Sparkles className="w-5 h-5 mt-0.5 shrink-0 text-violet-600" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-violet-800">
                    🎉 Promoción aplicada: {applicablePaymentPromo.name}
                    {applicablePaymentPromo.code && (
                      <span className="ml-2 font-mono text-xs text-violet-500">[{applicablePaymentPromo.code}]</span>
                    )}
                  </p>
                  <p className="text-xs text-violet-700 mt-0.5">
                    Descuento por forma de pago:{' '}
                    {applicablePaymentPromo.discountType === 'PERCENTAGE'
                      ? `${parseFloat(applicablePaymentPromo.discountValue).toFixed(0)}%`
                      : `$${parseFloat(applicablePaymentPromo.discountValue).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`}
                    {' '}→ ahorro: <strong>{formatCurrency(paymentPromoDiscount)}</strong>
                  </p>
                  <p className="text-xs text-violet-500 mt-1">
                    ✅ El descuento se aplicará automáticamente al registrar el pago.
                  </p>
                </div>
              </div>
            )}

            {/* Scenario badge for tuition invoices */}
            {tuitionPricing && scCfg && scenario && (
              <div className={`rounded-lg border p-3 flex items-start gap-3 ${scCfg.bg}`}>
                <scCfg.icon className={`w-5 h-5 mt-0.5 shrink-0 ${scCfg.color}`} />
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${scCfg.color}`}>{scCfg.label}</p>
                  <p className={`text-xs mt-0.5 ${scCfg.color} opacity-90`}>
                    {scCfg.desc(scenario === 'A' ? discountPct : surchargePct)}
                  </p>
                  <div className="flex gap-3 mt-2 text-xs flex-wrap">
                    <span className={scenario === 'A' ? 'font-bold underline' : 'opacity-60'}>
                      ✓ Pronto pago: {formatCurrency(getApplicableAmount('A', tuitionPricing, paymentSummary!.invoiceTotal, paymentSummary!.pendingAmount))}
                    </span>
                    <span className={scenario === 'B' ? 'font-bold underline' : 'opacity-60'}>
                      ⏳ Normal: {formatCurrency(paymentSummary!.invoiceTotal)}
                    </span>
                    <span className={scenario === 'C' ? 'font-bold underline' : 'opacity-60'}>
                      ⚠ Mora: {formatCurrency(getApplicableAmount('C', tuitionPricing, paymentSummary!.invoiceTotal, paymentSummary!.pendingAmount))}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="paymentMethod">Método de Pago</Label>
              <Select
                value={formData.paymentMethod}
                onValueChange={(value) => setFormData({ ...formData, paymentMethod: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getAvailablePaymentMethods(invoice).map((method) => (
                    <SelectItem key={method.value} value={method.value}>
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-gray-400" />
                        {method.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <SerieFolioSelector
              availableSeries={availableSeries}
              effectiveSerieId={effectiveSerieId}
              autoSerieId={autoSerieId}
              onChange={setManualSerieId}
            />

            <div className="space-y-2">
              <Label htmlFor="bankAccount">Cuenta de Destino</Label>
              <Select
                value={formData.bankAccountId}
                onValueChange={(value) => setFormData({ ...formData, bankAccountId: value })}
              >
                <SelectTrigger className={!formData.bankAccountId ? 'text-gray-400' : ''}>
                  <SelectValue placeholder="Seleccionar cuenta bancaria" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id.toString()}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="font-mono text-xs">{account.code}</span>
                        <span>{account.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference">Referencia (Opcional)</Label>
              <Input
                id="reference"
                placeholder="Ej: SPEI-123456, Cheque #789"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={paymentMutation.isPending}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={paymentMutation.isPending || !formData.bankAccountId || !effectiveSerieId}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {paymentMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Registrando...
                  </>
                ) : (
                  <>
                    <DollarSign className="w-4 h-4 mr-2" />
                    Registrar Pago
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
